import React from "react";
import {
  AppBar,
  Toolbar,
  Box,
  Typography,
  useTheme,
  alpha,
} from "@mui/material";
import { Link } from "react-router-dom";
import Navigation from "./Navigation";

interface HeaderProps {
  currentPage:
    | "dashboard"
    | "expenses"
    | "income"
    | "debts"
    | "goals"
    | "accounts"
    | "transactions"
    | "trends"
    | "groups"
    | "tools"
    | "settings";
  apiConnected: boolean;
  onToggleApi: () => void;
}

const Header: React.FC<HeaderProps> = ({
  currentPage,
  apiConnected,
  onToggleApi,
}) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        background: isDarkMode
          ? `linear-gradient(180deg, ${alpha(
              theme.palette.background.paper,
              0.95
            )} 0%, ${alpha(theme.palette.background.paper, 0.9)} 100%)`
          : `linear-gradient(180deg, ${alpha("#ffffff", 0.98)} 0%, ${alpha(
              "#ffffff",
              0.95
            )} 100%)`,
        backdropFilter: "blur(20px)",
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        boxShadow: isDarkMode
          ? `0 4px 20px ${alpha("#000000", 0.5)}`
          : `0 4px 20px ${alpha("#000000", 0.05)}`,
        transform: "none",
        "&:hover": {
          transform: "none",
          boxShadow: isDarkMode
            ? `0 4px 20px ${alpha("#000000", 0.5)}`
            : `0 4px 20px ${alpha("#000000", 0.05)}`,
        },
      }}
    >
      <Toolbar
        sx={{
          minHeight: { xs: 64, sm: 70 },
          px: { xs: 1, sm: 2 },
          width: "100%",
        }}
      >
        <Link
          to="/dashboard"
          style={{
            textDecoration: "none",
            color: "inherit",
            display: "flex",
            alignItems: "center",
            gap: "12px",
          }}
        >
          <Box
            component="img"
            src="/logo512.png"
            alt="Focus: Finance"
            sx={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              objectFit: "cover",
              boxShadow: isDarkMode
                ? `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`
                : `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`,
              transition: "all 0.3s ease",
              "&:hover": {
                transform: "rotate(-5deg) scale(1.05)",
                boxShadow: isDarkMode
                  ? `0 6px 16px ${alpha(theme.palette.primary.main, 0.4)}`
                  : `0 6px 16px ${alpha(theme.palette.primary.main, 0.3)}`,
              },
            }}
          />
          <Box>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 800,
                fontSize: { xs: "1.25rem", sm: "1.5rem" },
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                letterSpacing: "-0.5px",
                lineHeight: 1,
              }}
            >
              Focus: Finance
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.text.secondary,
                fontSize: "0.7rem",
                letterSpacing: "0.5px",
                textTransform: "uppercase",
                fontWeight: 500,
                display: { xs: "none", sm: "block" },
              }}
            >
              Financial Intelligence
            </Typography>
          </Box>
        </Link>

        <Navigation
          currentPage={currentPage}
          apiConnected={apiConnected}
          onToggleApi={onToggleApi}
        />
      </Toolbar>
    </AppBar>
  );
};

export default Header;
